import React, { useState, useEffect } from 'react'
import { Fa, Tooltip, showErrorMessage } from '@eltoro-ui/components'
import { Targetjobservicev1Audience } from 'next-gen-sdk'
import { useAppContext } from 'Contexts'
import { capitalize } from 'Helpers'

interface ParentAudienceTooltipProps {
  audienceId: string
  orgId: string
  className?: string
}

export const ParentAudienceTooltip: React.FC<ParentAudienceTooltipProps> = ({
  audienceId,
  orgId,
  className = '',
}) => {
  const [
    parentAudience,
    setParentAudience,
  ] = useState<Targetjobservicev1Audience>()
  const [loadingParentAudience, setLoadingParentAudience] = useState(false)
  const [parentAudienceHovered, setParentAudienceHovered] = useState(false)

  const { audienceServiceApi } = useAppContext()

  // Fetch parent audience when hovered
  useEffect(() => {
    if (
      parentAudienceHovered &&
      audienceId &&
      orgId &&
      !parentAudience &&
      audienceServiceApi
    ) {
      setLoadingParentAudience(true)
      audienceServiceApi
        .advertisingPlatformServiceGetAudience(audienceId, orgId)
        .then((response) => {
          setParentAudience(response)
        })
        .catch((error) => {
          showErrorMessage(
            'Error fetching parent audience',
            error?.body?.message
              ? capitalize(error?.body?.message)
              : 'There was an error fetching the parent audience. Please try again.',
          )
          console.error('Error fetching parent audience:', error)
        })
        .finally(() => {
          setLoadingParentAudience(false)
        })
    }
  }, [
    parentAudienceHovered,
    audienceId,
    orgId,
    audienceServiceApi,
    parentAudience,
  ])

  // Reset parent audience when audienceId changes
  useEffect(() => {
    setParentAudience(undefined)
    setParentAudienceHovered(false)
  }, [audienceId])

  return (
    <Tooltip
      position="bottomLeft"
      content={
        loadingParentAudience ? (
          <div className="flex items-center gap-2">
            <Fa icon="spinner" size="xs" />
            <span>Loading parent audience...</span>
          </div>
        ) : parentAudience ? (
          <div>
            <strong>Name:</strong> {parentAudience.name}
          </div>
        ) : (
          <div>Hover to load parent audience details</div>
        )
      }
    >
      <span
        className={`text-base-info-500 cursor-pointer hover:underline ${className}`}
        tabIndex={0}
        onMouseEnter={() => setParentAudienceHovered(true)}
        onMouseLeave={() => setParentAudienceHovered(false)}
        onFocus={() => setParentAudienceHovered(true)}
        onBlur={() => setParentAudienceHovered(false)}
      >
        {audienceId}
      </span>
    </Tooltip>
  )
}
